import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KeyRound, Trash2, Check, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";

/**
 * Seiful: un singur loc pentru secrete, și jurnalul cine-ce-când-de-ce.
 *
 * Componenta nu vede niciodată valorile — serverul nu le trimite. Ce se vede e
 * numele, cât e de lung, de câte ori a fost citit, și ce lipsește pe fiecare
 * integrare. Un câmp de parolă care s-ar reumple cu valoarea salvată ar fi
 * însemnat că valoarea circulă la fiecare deschidere a paginii.
 */
export default function SeifSettings({ workspaceId }) {
  const [date, setDate] = useState(null);
  const [jurnal, setJurnal] = useState(null);
  const [nume, setNume] = useState("");
  const [valoare, setValoare] = useState("");
  const [eroare, setEroare] = useState("");
  const [busy, setBusy] = useState(false);
  const [vedeJurnal, setVedeJurnal] = useState(false);

  const incarca = useCallback(async () => {
    try {
      const { data } = await api.get("/seif", { params: { workspace_id: workspaceId } });
      setDate(data);
      setEroare("");
    } catch (e) {
      setDate(null);
      setEroare(e?.response?.data?.detail || "Nu am putut citi seiful");
    }
  }, [workspaceId]);

  useEffect(() => { if (workspaceId) incarca(); }, [workspaceId, incarca]);

  useEffect(() => {
    if (!vedeJurnal || !workspaceId) return;
    api.get("/seif/acces", { params: { workspace_id: workspaceId } })
      .then((r) => setJurnal(r.data))
      .catch(() => setJurnal(null));
  }, [vedeJurnal, workspaceId, date]);

  const pune = async () => {
    setBusy(true);
    try {
      await api.put("/seif", { workspace_id: workspaceId, nume: nume.trim(), valoare });
      // Valoarea se uită imediat din memoria paginii. Nu e teatru: un câmp
      // rămas plin ajunge în următorul screenshot.
      setValoare("");
      setNume("");
      toast.success("Secret salvat în seif");
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut salva");
    } finally { setBusy(false); }
  };

  const sterge = async (n) => {
    setBusy(true);
    try {
      await api.delete(`/seif/${encodeURIComponent(n)}`, { params: { workspace_id: workspaceId } });
      toast.success(`${n} scos din seif`);
      await incarca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut șterge");
    } finally { setBusy(false); }
  };

  if (!date && !eroare) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="seif-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Seiful de credențiale</h3>
        <span className="label-tiny ml-2">// CRIPTATE, CU JURNAL DE ACCES</span>
        <button
          type="button" data-testid="seif-vezi-jurnal" onClick={() => setVedeJurnal((v) => !v)}
          className="ml-auto flex items-center gap-1 text-[11px] text-brand-2 hover:text-brand"
        ><Eye className="w-3 h-3" /> vezi jurnalul</button>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-xs text-fg/50 leading-relaxed">
          Valorile nu ies de aici: nici în pagina asta, nici în loguri, nici în răspunsuri.
          Fiecare citire se înregistrează cu unealta și motivul ei, iar o citire fără motiv
          declarat e refuzată.
        </p>

        {eroare && <p className="text-xs text-red-400" data-testid="seif-eroare">{eroare}</p>}

        {date && (
          <>
            <div className="space-y-2" data-testid="seif-domenii">
              {date.domenii.map((d) => (
                <div key={d.domeniu} className="text-xs flex items-start gap-2">
                  {d.complet
                    ? <Check className="w-3.5 h-3.5 text-brand mt-0.5 flex-shrink-0" />
                    : <AlertTriangle className="w-3.5 h-3.5 text-fg/30 mt-0.5 flex-shrink-0" />}
                  <span>
                    <span className={d.complet ? "text-fg" : "text-fg/60"}>{d.eticheta}</span>
                    {!d.complet && (
                      <span className="text-fg/40"> — lipsesc de aici: <span className="mono">{d["lipsă"].join(", ")}</span></span>
                    )}
                    <span className="block text-fg/40">{d.nota}</span>
                  </span>
                </div>
              ))}
            </div>

            {date.secrete.length > 0 && (
              <ul className="border-t border-line/10 pt-3 space-y-1" data-testid="seif-lista">
                {date.secrete.map((s) => (
                  <li key={s.nume} className="flex items-center gap-3 text-sm">
                    <span className="mono text-fg/80">{s.nume}</span>
                    <span className="text-[11px] text-fg/30">
                      {s.lungime} caractere · citit de {s.citiri} ori până acum
                    </span>
                    <button
                      type="button" onClick={() => sterge(s.nume)} disabled={busy}
                      data-testid={`seif-sterge-${s.nume}`} aria-label={`Șterge ${s.nume}`}
                      className="ml-auto text-fg/30 hover:text-red-400 disabled:opacity-40"
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}

            <div className="border-t border-line/10 pt-3 flex items-end gap-2">
              <div className="flex-1">
                <label className="label-tiny block mb-1" htmlFor="seif-nume">Nume (majuscule și underscore)</label>
                <input id="seif-nume" data-testid="seif-nume" className="input-flat mono text-sm"
                       value={nume}
                       onChange={(e) => setNume(e.target.value.toUpperCase())} />
              </div>
              <div className="flex-1">
                <label className="label-tiny block mb-1" htmlFor="seif-valoare">Ce se pune în seif</label>
                <input id="seif-valoare" data-testid="seif-valoare" type="password"
                       autoComplete="new-password" className="input-flat text-sm"
                       value={valoare} onChange={(e) => setValoare(e.target.value)} />
              </div>
              <button type="button" data-testid="seif-pune" onClick={pune}
                      disabled={busy || !nume.trim() || !valoare}
                      className="px-3 py-2 text-sm border border-brand/50 text-brand hover:bg-brand/10 disabled:opacity-40">
                Pune în seif
              </button>
            </div>
          </>
        )}

        {vedeJurnal && jurnal && (
          <div className="border-t border-line/10 pt-3" data-testid="seif-jurnal">
            <p className="text-xs text-fg/50 mb-2">
              {jurnal.total} citiri în ultimele {jurnal.zile} de zile
            </p>
            {jurnal.total === 0 ? (
              <p className="text-xs text-fg/40" data-testid="seif-jurnal-gol">
                Nicio citire înregistrată încă.
              </p>
            ) : (
              <ul className="space-y-1 max-h-56 overflow-y-auto">
                {jurnal.randuri.map((r) => (
                  <li key={r.id} className="text-xs text-fg/60">
                    <span className="mono text-fg/40">{(r.la || "").slice(0, 16).replace("T", " ")}</span>{" "}
                    <span className="text-fg/80">{r.unealta}</span>{" → "}
                    <span className="mono">{r.nume}</span>
                    {!r.gasit && <span className="text-amber-400"> (negăsit)</span>}
                    <span className="block text-fg/40 pl-2">„{r.motiv}"</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
